import { Popover } from "bootstrap";
import Cookies from "js-cookie";
import { version } from "../../package.json";

import { SimpleRecorder } from "./recording.js";
import { RecordingTester } from "./recording_tester.js";
import { SetupCheck, BlackFrameDetector, FrameRateMeter } from "./setup_check.js";
import { SimpleTimer } from "./timer.js";
import { EventBus } from "./event_bus.js";
import { Effects } from "./effects.js";
import { Utils } from "./utils.js";
import { MessageBus } from "./message_bus.js"
import { APP } from "./app.js";

export class RecordingManager {
  // Controls enabled in each state. Every other control is disabled.
  static ENABLED_CONTROLS = {
    idle: ["test", "replay", "start", "mic", "changeScreen", "retest"],
    preparing: [],
    testing: ["mic"],
    replaying: ["mic"],
    recording: ["pause", "finish", "mic", "comment", "preview"],
    paused: ["resume", "finish", "mic", "comment", "preview"],
    interrupted: ["resume", "finish", "mic", "comment"],
    finishing: [],
    finished: [],
  };

  // Mic meter level (0-100) from which the tester counts as heard
  static MIC_HEARD_LEVEL = 10;

  // Repeated wherever the tester is about to see the screen picker (C1)
  static PICKER_GUIDANCE = "In the browser dialog choose *Entire screen* and tick *Share system audio*.";

  static PROFILE_COOKIE_DAYS = 30;

  videoStatesBlock
  videoStatePlayingBadge
  videoStateRecordingBadge
  videoStatePausedBadge
  testCountdown
  videoButtonsBlock
  testRecordingButton
  testReplayButton
  startRecordingButton
  changeScreenButton
  retestButton
  recorderButtonsBlock
  pauseButton
  resumeButton
  finishRecordingButton
  videoMiniControlsBlock
  micCheck
  micCheckBlock
  micPopover
  micLevel
  micLevelBar
  commentButton
  timerDisplay
  uploadingProgressBlock
  replayQuestionsBlock
  replayQuestionVoice
  replayQuestionGame
  videoElement
  sessionStatus
  previewToggleButton
  roomElement
  controls

  timer
  state
  pausedBy
  isMicActive
  micAttempt
  micPermission
  isRecordingBlocks
  blocksPromise
  testBlobSize
  testDone
  frameRateTest
  previewWanted
  sessionFinalizedPromise
  missingCapabilities

  recorder
  recordingTester
  setupCheck
  blackFrameDetector
  frameRateMeter
  micMeterIntervalID
  // videoSettingsTrackIntervalID

  TESTING_SECONDS
  VIDEO_BLOCK_SECONDS

  // The last recording info sent, and the requests in flight one after the other
  #lastRecordingInfo = null;
  #recordingInfoChain = Promise.resolve();

  constructor() {
    this.TESTING_SECONDS = 10;
    this.VIDEO_BLOCK_SECONDS = 60;

    this.videoStatesBlock = document.querySelector("#video-state-badges");
    this.videoStatePlayingBadge = document.querySelector("#video-state-playing-badge");
    this.videoStateRecordingBadge = document.querySelector("#video-state-recording-badge");
    this.videoStatePausedBadge = document.querySelector("#video-state-paused-badge");
    this.testCountdown = document.querySelector("#test-countdown");
    this.videoButtonsBlock = document.querySelector("#video-buttons-wrapper");
    this.testRecordingButton = document.querySelector("#test-recording-button");
    this.testReplayButton = document.querySelector("#test-replay-button");
    this.startRecordingButton = document.querySelector("#start-recording-button");
    this.changeScreenButton = document.querySelector("#change-screen-button");
    this.retestButton = document.querySelector("#retest-button");
    this.recorderButtonsBlock = document.querySelector("#recorder-buttons");
    this.pauseButton = document.querySelector("#pause-button");
    this.resumeButton = document.querySelector("#resume-button");
    this.finishRecordingButton = document.querySelector("#finish-recording-button");
    this.videoMiniControlsBlock = document.querySelector("#video-mini-controls");
    this.micCheck = document.querySelector("#mic-check");
    this.micCheckBlock = document.querySelector("#mic-check-wrapper");
    this.micPopover = Popover.getOrCreateInstance("#mic-check-wrapper")
    this.micLevel = document.querySelector("#mic-level");
    this.micLevelBar = this.micLevel.querySelector(".progress-bar");
    this.commentButton = document.querySelector("#timed-comment-button");
    this.timerDisplay = document.querySelector("#timer-display");
    this.uploadingProgressBlock = document.querySelector("#uploading-progress-block");
    this.replayQuestionsBlock = document.querySelector("#replay-questions");
    this.replayQuestionVoice = this.replayQuestionsBlock.querySelector('[data-testid="replay-question-voice"]');
    this.replayQuestionGame = this.replayQuestionsBlock.querySelector('[data-testid="replay-question-game"]');

    this.videoElement = document.querySelector("#video-wrapper video");
    this.videoElement.controls = false;
    this.sessionStatus = document.querySelector("#session-status");
    this.previewToggleButton = document.querySelector("#preview-toggle-button");
    this.roomElement = document.querySelector("#recording-room");

    // The replay answers are not controls: the tester answers while the replay plays
    this.controls = {
      test: this.testRecordingButton,
      replay: this.testReplayButton,
      start: this.startRecordingButton,
      changeScreen: this.changeScreenButton,
      retest: this.retestButton,
      preview: this.previewToggleButton,
      pause: this.pauseButton,
      resume: this.resumeButton,
      finish: this.finishRecordingButton,
      mic: this.micCheck,
      comment: this.commentButton,
    };

    this.pausedBy = null;
    this.micAttempt = 0;
    this.micPermission = null;
    this.isRecordingBlocks = false;
    this.blocksPromise = Promise.resolve();
    this.testBlobSize = null;
    this.testDone = false;
    this.frameRateTest = null;
    this.previewWanted = false;
    this.sessionFinalizedPromise = null;

    this.recorder = new SimpleRecorder();
    this.recordingTester = new RecordingTester(this.videoElement, this.recorder);
    this.blackFrameDetector = new BlackFrameDetector(this.videoElement);
    this.frameRateMeter = new FrameRateMeter(this.videoElement);
    this.setupCheck = new SetupCheck(document.querySelector("#setup-check"), () => { this.setupCheckChanged(); });

    this.setState("idle");
    this.setMicToggle(false);

    this.timer = new SimpleTimer(() => { this.timerTick(); });

    this.setListeners();
    this.setEvents();

    this.checkBrowser();
    this.checkMicPermission();
  }

  // Read by the beforeunload guard in page.js. "finishing" counts because the
  // last block is not in the upload queue yet.
  get isRecording() {
    return ["recording", "paused", "interrupted", "finishing"].includes(this.state);
  }

  // Read by page.js before it offers the "I'm in" button (C9)
  get isBrowserSupported() {
    return this.missingCapabilities.length === 0;
  }

  setState(state, busyButton = null) {
    console.log(`RecordingManager.setState(${this.state} -> ${state})`);
    this.state = state;
    // For the CSS: the timer and the stage frame show the state
    this.roomElement.dataset.state = state;

    const enabledControls = RecordingManager.ENABLED_CONTROLS[state];

    Object.entries(this.controls).forEach(([name, element]) => {
      element.disabled = !enabledControls.includes(name);
      element.querySelectorAll(".spinner-border").forEach((spinner) => spinner.remove());
    });

    if (busyButton !== null) {
      busyButton.insertAdjacentHTML("afterbegin", '<span class="spinner-border spinner-border-sm" aria-hidden="true"></span> ');
    }
  }

  setListeners() {
    EventBus.addListener(
      "timed-comment-toggle-recording-pause",
      (value) => { this.askingForPause(value); }
    );

    EventBus.addListener(
      "screen-share-ended",
      () => { this.screenShareEnded(); }
    );

    EventBus.addListener(
      "mic-ended",
      () => { this.micEnded(); }
    );
  }

  setEvents() {
    this.testRecordingButton.addEventListener("click", (event) => {
      this.testRecording();
    });

    this.testReplayButton.addEventListener("click", (event) => {
      this.playVideo();
    });

    this.changeScreenButton.addEventListener("click", (event) => {
      this.changeScreen();
    });

    this.retestButton.addEventListener("click", (event) => {
      this.testRecording();
    });

    this.previewToggleButton.addEventListener("click", (event) => {
      this.setPreviewWanted(!this.previewWanted);
    });

    this.micCheck.addEventListener("change", (event) => {
      this.setMicActive(this.micCheck.checked);
    });

    this.startRecordingButton.addEventListener("click", (event) => {
      console.log("startRecordingButton.click()");
      this.startRecording();
    });

    this.pauseButton.addEventListener("click", (event) => {
      this.pauseRecording("user");
    });

    this.resumeButton.addEventListener("click", (event) => {
      this.resumeRecording();
    });

    this.finishRecordingButton.addEventListener("click", (event) => {
      this.finishRecording();
    });

    this.replayQuestionsBlock.querySelectorAll("input[type=radio]").forEach((input) => {
      input.addEventListener("change", (event) => {
        this.replayAnswered(input);
      });
    });

    // Replay of the test video. Registered once and guarded by the state, so
    // the live preview during a test or a session is ignored (A9, A26).
    this.videoElement.addEventListener("play", (event) => {
      if (this.state === "idle" && this.videoElement.srcObject === null) {
        this.replayStarted();
      }
    });

    this.videoElement.addEventListener("pause", (event) => {
      this.replayStopped();
    });

    // Scrubbing the replay changes how many frames it shows
    this.videoElement.addEventListener("seeking", (event) => {
      if (this.frameRateTest !== null && this.videoElement.srcObject === null) {
        this.frameRateTest.seeked = true;
      }
    });

    this.videoElement.addEventListener("ended", (event) => {
      this.replayEnded();
      this.replayStopped();
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    });

    this.videoElement.addEventListener("error", (event) => {
      if (this.state === "replaying") {
        this.replayFailed(this.videoElement.error);
      }
    });
  }

  // Setup Check :: INI
  checkBrowser() {
    this.missingCapabilities = SimpleRecorder.missingCapabilities();
    this.setupCheck.update({ browserMissing: this.missingCapabilities });
  }

  // A mic blocked in an earlier visit shows up before the tester clicks
  // anything (C2). Never rejects.
  async checkMicPermission() {
    try {
      this.micPermission = await navigator.permissions.query({ name: "microphone" });
    } catch (error) {
      // Firefox cannot query the microphone permission
      console.log("RecordingManager.checkMicPermission()", error);
      return;
    }

    const apply = () => {
      this.setupCheck.update({ micBlocked: this.micPermission.state === "denied" });
    };

    apply();
    this.micPermission.addEventListener("change", apply);
  }

  setupCheckChanged() {
    this.updateStartButton();
    this.updateMicPopover();
  }

  // One primary action at a time: Test until a test ran with no failing row,
  // then Start, in green. Start stays enabled either way.
  updateStartButton() {
    const isReady = this.testDone && !this.setupCheck.hasFailures();
    this.startRecordingButton.classList.toggle("btn-success", isReady);
    this.startRecordingButton.classList.toggle("btn-outline-primary", !isReady);
    this.testRecordingButton.classList.toggle("btn-primary", !isReady);
    this.testRecordingButton.classList.toggle("btn-outline-light", isReady);
  }

  updateScreenFacts() {
    const track = this.recorder.isScreenLive() ? this.recorder.screenVideoTrack : null;

    this.setupCheck.update({
      screen: track && { surface: track.getSettings().displaySurface ?? null, label: track.label },
      gameAudio: this.recorder.screenAudioTrack !== null,
    });
  }

  updateMicFacts() {
    this.setupCheck.update({
      micOn: this.isMicActive,
      micLive: this.recorder.micStream !== null,
    });
  }

  // A new screen was picked: check it from scratch
  screenShared() {
    this.frameRateTest = null;
    this.setupCheck.update({ picture: null, gameHeard: null, screenProblem: null, quality: null });
    this.updateScreenFacts();
    this.changeScreenButton.classList.remove("d-none");
  }

  // The shared screen is gone, and so are the checks made with it
  screenReleased() {
    this.testDone = false;
    this.changeScreenButton.classList.add("d-none");
    this.hideReplayQuestions();
    this.frameRateTest = null;
    this.setupCheck.update({ picture: null, gameHeard: null, quality: null });
    this.updateScreenFacts();
    this.updateStartButton();
  }

  // A verdict about a screen that is no longer shared would describe nothing
  pictureChecked(verdict) {
    if (this.recorder.isScreenLive()) {
      this.setupCheck.update({ picture: verdict });
    }
  }

  // Asked after the test. The answers only update the checklist: nothing is
  // sent, they are for the tester.
  showReplayQuestions() {
    const askVoice = this.recorder.micStream !== null;
    const askGame = this.recorder.screenAudioTrack !== null;

    this.replayQuestionVoice.classList.toggle("d-none", !askVoice);
    this.replayQuestionGame.classList.toggle("d-none", !askGame);
    this.replayQuestionsBlock.classList.toggle("d-none", !askVoice && !askGame);
  }

  hideReplayQuestions() {
    this.replayQuestionsBlock.classList.add("d-none");
    this.replayQuestionsBlock.querySelectorAll("input[type=radio]").forEach((input) => { input.checked = false; });
  }

  replayAnswered(input) {
    const heard = input.value === "yes";

    if (input.name === "replay-voice") {
      this.setupCheck.update({ voiceHeard: heard });
    } else {
      this.setupCheck.update({ gameHeard: heard });
    }
  }
  // Setup Check :: END

  // Reuses the screen while it is still shared, so Start after a test does
  // not open the picker again (C8). Screen first, because getDisplayMedia
  // needs the click's user gesture. A mic failure does not stop the
  // recording: the toggle goes off and the tester is told why.
  async acquireStreams() {
    this.recorder.releaseFullStream();

    if (!this.recorder.isScreenLive()) {
      await this.recorder.initScreenStream();
      this.screenShared();
    }

    if (this.isMicActive) {
      try {
        await this.recorder.initMicStream();
      } catch (error) {
        if (this.isMicActive) {
          this.micFailed(error);
        }
      }

      this.updateMicFacts();
    }

    await this.recorder.prepareAudio();

    // Sharing stopped while the browser was still asking for the mic
    if (!this.recorder.isScreenLive()) {
      throw SimpleRecorder.error("ScreenShareEnded", "Screen sharing stopped before the recording started");
    }
  }

  // Test Recording :: INI
  async testRecording() {
    if (this.state !== "idle") {
      return;
    }

    this.setState("preparing", this.testRecordingButton);

    try {
      await this.acquireStreams();
    } catch (error) {
      this.setState("idle");
      this.showRecordingError(error, "screen");
      return;
    }

    this.setState("testing");
    this.testDone = false;
    this.frameRateTest = null;
    this.hideReplayQuestions();
    this.setupCheck.update({ picture: null, voiceHeard: null, gameHeard: null, quality: null });
    this.updateStartButton();

    this.showBadge(this.videoStateRecordingBadge);
    this.testCountdown.classList.remove("d-none");
    Effects.delayedFadeOut(this.videoButtonsBlock);
    Effects.delayedBlurOut(this.videoElement);
    this.videoElement.controls = false;
    this.timer.start();

    let blob;
    const startedAt = performance.now();

    try {
      const recording = this.recordingTester.record(this.TESTING_SECONDS);
      // Started after record(), which put the live preview they sample in place
      this.blackFrameDetector.start((verdict) => { this.pictureChecked(verdict); });
      this.frameRateMeter.start(this.recorder.screenVideoTrack);
      blob = await recording;
    } catch (error) {
      this.blackFrameDetector.stop();
      this.frameRateMeter.stop();
      this.timer.pause();
      this.testCountdown.classList.add("d-none");
      this.recorder.releaseFullStream();
      this.videoElement.srcObject = null;
      this.setState("idle");
      this.showBadge(null);
      this.testRecordingVideoFinished();
      this.showRecordingError(error, "recorder");
      return;
    }

    // The last look at the live preview, before the replay replaces it
    this.pictureChecked(this.blackFrameDetector.stop());
    const capture = this.frameRateMeter.stop();
    const recordedSeconds = (performance.now() - startedAt) / 1000;
    this.timer.pause();
    this.testCountdown.classList.add("d-none");

    // The screen and the mic stay shared for the session (C8)
    this.recorder.releaseFullStream();
    this.testBlobSize = blob.size;
    this.testDone = this.recorder.isScreenLive();
    this.updateStartButton();

    if (this.testDone) {
      this.showReplayQuestions();
      this.captureMeasured(capture, recordedSeconds);
    }

    this.showBadge(null);
    this.videoElement.controls = true;

    try {
      await this.recordingTester.play(blob);
      if (this.state === "testing") {
        this.replayStarted();
      }
    } catch (error) {
      this.replayFailed(error);
    }
  }

  // Frame Rate :: INI
  // A still screen is told at once; the rest waits for the end of the replay,
  // which tells what the recording kept (D4, D7)
  captureMeasured(capture, recordedSeconds) {
    this.frameRateTest = { ...capture, recordedSeconds, seeked: false, done: false };
    this.setupCheck.update({ quality: this.qualityFacts(null) });
  }

  // The first end of the replay after a test: every frame was shown once
  replayEnded() {
    const test = this.frameRateTest;

    if (test === null || test.done || this.videoElement.srcObject !== null) {
      return;
    }

    test.done = true;
    const frames = test.seeked ? null : this.recordingTester.playedFrames();
    const encodeFps = frames === null ? null : frames / test.recordedSeconds;

    this.frameRateMeasured(encodeFps);
  }

  // Steps the resolution down when the encoder lost frames the screen
  // delivered, and tells the developer what was measured
  frameRateMeasured(encodeFps) {
    const { captureFps, averageFps, deliveredFrames, discardedFrames } = this.frameRateTest;
    const quality = this.qualityFacts(encodeFps);
    const lower = this.recorder.lowerProfile();

    if (FrameRateMeter.encoderIsBottleneck(captureFps, encodeFps, averageFps) && lower !== null) {
      quality.steppedDownTo = lower;
      this.changeProfile(lower);
    }

    this.setupCheck.update({ quality });

    MessageBus.debug("Frame rate test: " + JSON.stringify({
      captureFps: RecordingManager.roundFps(captureFps),
      averageFps: RecordingManager.roundFps(averageFps),
      encodeFps: RecordingManager.roundFps(encodeFps),
      profile: quality.profile.name,
      codec: this.recorder.mimeType,
      width: quality.width,
      height: quality.height,
      deliveredFrames,
      discardedFrames,
      steppedDownTo: quality.steppedDownTo?.name ?? null,
    }));
  }

  qualityFacts(encodeFps) {
    const settings = this.recorder.screenVideoTrack?.getSettings() ?? {};

    return {
      captureFps: this.frameRateTest.captureFps,
      averageFps: this.frameRateTest.averageFps,
      encodeFps,
      width: settings.width ?? null,
      height: settings.height ?? null,
      profile: this.recorder.profile,
      steppedDownTo: null,
    };
  }

  static roundFps(fps) {
    return fps === null ? null : Math.round(fps * 10) / 10;
  }

  // Applies to the screen already shared, the next recorder, and a reload
  changeProfile(profile) {
    this.recorder.setProfile(profile).catch((error) => {
      MessageBus.debug("RecordingManager.changeProfile() - applyConstraints failed: " + Utils.normalizeError(error).message);
    });

    try {
      Cookies.set(this.profileCookieName(), profile.name, { expires: RecordingManager.PROFILE_COOKIE_DAYS });
    } catch (error) {
      console.warn("RecordingManager.changeProfile() - cookie", error);
    }
  }

  // Called once the Playtest Round is loaded: a profile chosen by an earlier
  // test of the same Playtest Round is kept
  restoreProfile() {
    try {
      const profile = SimpleRecorder.profileByName(Cookies.get(this.profileCookieName()));

      if (profile !== null) {
        this.recorder.profile = profile;
        MessageBus.debug("RecordingManager.restoreProfile() - " + profile.name);
      }
    } catch (error) {
      console.warn("RecordingManager.restoreProfile()", error);
    }
  }

  profileCookieName() {
    return "recording-profile-" + APP.playcocolaAPI.playtestRound.uuid;
  }
  // Frame Rate :: END

  replayStarted() {
    this.setState("replaying");
    this.showBadge(this.videoStatePlayingBadge);
    Effects.delayedFadeOut(this.videoButtonsBlock);
    Effects.delayedFadeOut(this.micCheckBlock, 0);
    Effects.delayedBlurOut(this.videoElement);
  }

  // Paused or ended: bring Test and Start back
  replayStopped() {
    if (this.state !== "replaying") {
      return;
    }

    this.setState("idle");
    this.showBadge(null);
    this.testRecordingVideoFinished();
  }

  replayFailed(error) {
    if (this.state !== "replaying" && this.state !== "testing") {
      return;
    }

    this.setState("idle");
    this.showBadge(null);
    this.testRecordingVideoFinished();

    // Interrupted by a pause, or autoplay blocked: the Replay button is enough
    if (error?.name === "AbortError" || error?.name === "NotAllowedError") {
      MessageBus.debug("RecordingManager.replayFailed() - " + error.name);
      return;
    }

    MessageBus.debug("RecordingManager.replayFailed() - test video blob size: " + this.testBlobSize);
    MessageBus.error("The test video could not be played, please test again.", error);
  }

  testRecordingVideoFinished() {
    Effects.delayedFadeIn(this.testReplayButton);
    Effects.delayedFadeIn(this.videoButtonsBlock);
    Effects.delayedFadeIn(this.micCheckBlock);
    Effects.delayedBlurIn(this.videoElement);
  }

  // Picks another screen and checks it straight away with a new test
  changeScreen() {
    if (this.state !== "idle") {
      return;
    }

    this.recorder.closeScreen();
    this.screenReleased();
    this.testRecording();
  }
  // Test Recording :: END

  // Mic :: INI
  async setMicActive(value) {
    console.log("App.setMicActive(): ", value);
    const attempt = ++this.micAttempt;

    this.setMicToggle(value);
    this.setupCheck.update({ micProblem: null, micStopped: false, micHeard: false, voiceHeard: null });
    this.updateMicFacts();
    MessageBus.debug("Mic Activation: " + value);

    try {
      await this.recorder.setMicActive(value);
    } catch (error) {
      // Only the latest click counts, and only once
      if (attempt === this.micAttempt && this.isMicActive) {
        this.micFailed(error);
      }
      return;
    }

    this.updateMicFacts();

    if (attempt === this.micAttempt) {
      this.sendRecordingInfo("microphone");
    }
  }

  setMicToggle(value) {
    this.micCheck.checked = value;
    this.isMicActive = value;
    this.micLevel.classList.toggle("d-none", !value);
    this.updateMicPopover();

    if (value) {
      this.micMeterStart();
    } else {
      this.micMeterStop();
    }
  }

  // Also after a failure or an unplugged mic: the session carries on without it
  switchMicOff() {
    this.recorder.isMicActive = false;
    this.recorder.releaseMic();
    this.setMicToggle(false);
    this.updateMicFacts();
    this.sendRecordingInfo("microphone");
  }

  micFailed(error) {
    this.switchMicOff();
    this.showRecordingError(error, "microphone");
  }

  // The mic was unplugged: carry on without it and say so without blocking (A2)
  micEnded() {
    MessageBus.debug("RecordingManager.micEnded() in state " + this.state);
    this.switchMicOff();
    this.setupCheck.update({ micStopped: true });
    this.micPopover.show();
  }

  updateMicPopover() {
    let content = "Mic is deactivated, click to activate it.";

    if (this.micCheck.checked) {
      content = "Mic is active, click to deactivate it.";
    } else if (this.setupCheck.facts.micStopped) {
      content = "Your microphone stopped. The recording continues without it. Click to switch it on again.";
    } else if (this.setupCheck.state("microphone") === "fail") {
      content = "The microphone cannot be used, see How to fix in the setup check. Click to try again.";
    }

    this.micPopover.setContent({ ".popover-body": content });
  }
  // Mic :: END

  timerTick(timer) {
    this.timerDisplay.innerHTML = this.timer.timeToString();

    if (this.state === "testing") {
      const secondsLeft = Math.max(0, Math.ceil(this.TESTING_SECONDS - this.timer.getTime() / 1000));
      this.testCountdown.textContent = `Recording test… ${secondsLeft} s`;
    }
  }

  // Shows one state badge, or none, and hides the others
  showBadge(badge) {
    [this.videoStatePlayingBadge, this.videoStateRecordingBadge, this.videoStatePausedBadge].forEach((element) => {
      if (element === badge) {
        Effects.delayedFadeIn(element, 0);
      } else {
        Effects.delayedFadeOut(element, 0);
      }
    });
  }

  playVideo() {
    this.videoElement.play().catch((error) => console.warn("RecordingManager.playVideo()", error));
  }

  // Live Preview :: INI
  // During the session the live preview is off unless asked for: decoding and
  // painting the capture, blurred, competes with the game (D5). A status card
  // takes its place.
  setPreviewWanted(value) {
    this.previewWanted = value;
    this.updatePreview();
  }

  // After Continue the full stream is built here, before the recorder needs it
  updatePreview() {
    const stream = this.previewWanted ? this.recorder.getFullStream() : null;

    if (this.videoElement.srcObject !== stream) {
      this.videoElement.srcObject = stream;
      this.videoElement.muted = true;
      this.videoElement.controls = false;
    }

    this.videoElement.classList.toggle("d-none", stream === null);
    this.sessionStatus.classList.toggle("d-none", stream !== null);
    this.previewToggleButton.textContent = this.previewWanted ? "Hide preview" : "Show preview";

    if (this.state === "recording") {
      this.playPreview();
    }
  }

  // Never plays the test video still loaded in the element
  playPreview() {
    if (this.videoElement.srcObject !== null) {
      this.playVideo();
    }
  }
  // Live Preview :: END

  // Session :: INI
  async startRecording() {
    if (this.state !== "idle") {
      return;
    }

    this.setState("preparing", this.startRecordingButton);

    try {
      await this.acquireStreams();
    } catch (error) {
      this.setState("idle");
      this.showRecordingError(error, "screen");
      return;
    }

    this.hideReplayQuestions();
    this.sendTracksInfo();
    MessageBus.debug("Setup check: " + JSON.stringify(this.setupCheck.states()));
    this.setState("recording");

    Effects.delayedFadeOut(this.videoButtonsBlock);
    Effects.delayedBlurOut(this.videoElement);

    Effects.delayedFadeIn(this.commentButton);
    Effects.delayedFadeIn(this.recorderButtonsBlock);
    Effects.delayedFadeIn(this.previewToggleButton);
    this.showBadge(this.videoStateRecordingBadge);

    this.timer.start();
    this.updatePreview();
    this.recordBlocks();
    // The recorder has started: its codec and bitrates are known
    this.sendRecordingInfo("start");
  }

  // Starts the block loop unless one is running. A running loop simply
  // carries on, so a quick Pause + Resume never starts two recorders (A11).
  recordBlocks() {
    if (this.isRecordingBlocks) {
      return;
    }

    this.blocksPromise = this.recordBlocksLoop();
  }

  async recordBlocksLoop() {
    this.isRecordingBlocks = true;

    try {
      while (this.state === "recording") {
        const blob = await this.recorder.record(this.VIDEO_BLOCK_SECONDS);
        await this.newVideoBlock(blob);
      }
    } catch (error) {
      this.blockFailed(error);
    } finally {
      this.isRecordingBlocks = false;
    }
  }

  async newVideoBlock(blob) {
    console.log("RecordingManager.newVideoBlock().blob.size: ", blob.size);
    APP.playcocolaAPI.sendDebugEvent("RecordingManager.newVideoBlock().blob.size: " + blob.size);

    // A block stopped before its first video frame holds no footage, and the
    // server's ffmpeg cannot open it: the whole recording would fail (A31)
    if (!(await SimpleRecorder.hasMedia(blob))) {
      MessageBus.debug("RecordingManager.newVideoBlock(): skipped a part with no media, " + blob.size + " bytes");
      return;
    }

    APP.uploader.enqueue(blob);
  }

  blockFailed(error) {
    if (this.state === "recording" || this.state === "paused") {
      this.interrupt();
      MessageBus.error("The recording stopped unexpectedly. Click **Continue** to carry on.", error);
    } else {
      MessageBus.debug("RecordingManager.blockFailed() in state " + this.state + ": " + Utils.normalizeError(error).message);
    }
  }

  pauseRecording(by = "user") {
    if (this.state !== "recording") {
      return;
    }

    this.setState("paused");
    this.pausedBy = by;
    APP.playcocolaAPI.sendDebugEvent("RecordingManager.pauseRecording() by " + by);

    this.showBadge(this.videoStatePausedBadge);

    this.timer.pause();
    this.recorder.stop();
    this.videoElement.pause();
    this.micMeterStop();
    Effects.delayedBlurIn(this.videoElement);
  }

  resumeRecording() {
    if (this.state === "interrupted") {
      this.recoverFromInterruption();
      return;
    }

    if (this.state !== "paused") {
      return;
    }

    this.setState("recording");
    this.pausedBy = null;
    APP.playcocolaAPI.sendDebugEvent("RecordingManager.resumeRecording()");

    this.showRecordingResumed();
    this.recordBlocks();
  }

  showRecordingResumed() {
    this.showBadge(this.videoStateRecordingBadge);
    this.timer.resume();
    this.playPreview();

    if (this.isMicActive) {
      this.micMeterStart();
    }

    Effects.delayedBlurOut(this.videoElement);
  }

  // Screen sharing stopped or the recorder failed. The current block (if any)
  // stops and uploads; Continue picks up from here.
  interrupt() {
    const wasRecording = this.state === "recording";

    this.setState("interrupted");
    this.pausedBy = null;
    this.recorder.stop();
    this.videoElement.pause();

    if (wasRecording) {
      this.timer.pause();
      this.showBadge(this.videoStatePausedBadge);
      this.micMeterStop();
      Effects.delayedBlurIn(this.videoElement);
    }
  }

  screenShareEnded() {
    MessageBus.debug("RecordingManager.screenShareEnded() in state " + this.state);

    if (this.state === "recording" || this.state === "paused") {
      this.interrupt();
      MessageBus.info(`Screen sharing stopped. Click **Continue** to choose your screen again and carry on.\n\n${RecordingManager.PICKER_GUIDANCE}`);
    } else if (this.state === "testing") {
      // Replay what was captured so far
      this.recorder.stop();
    } else if (this.state === "idle" || this.state === "replaying") {
      this.recorder.closeScreen();
    }

    this.screenReleased();
  }

  // getDisplayMedia needs a fresh user gesture, so this runs from the Continue
  // click. A screen that is still shared (the recorder failed) is reused.
  async recoverFromInterruption() {
    this.setState("preparing", this.resumeButton);

    try {
      await this.blocksPromise;
      await this.acquireStreams();
    } catch (error) {
      this.setState("interrupted");
      this.showRecordingError(error, "screen");
      return;
    }

    this.sendTracksInfo();
    this.setState("recording");
    APP.playcocolaAPI.sendDebugEvent("RecordingManager.recoverFromInterruption()");

    // The new screen, if the preview is on
    this.updatePreview();
    this.showRecordingResumed();
    this.recordBlocks();
    this.sendRecordingInfo("continue");
  }

  async finishRecording() {
    if (!["recording", "paused", "interrupted"].includes(this.state)) {
      return;
    }

    if (!confirm("Please confirm you want to finish the Play Session")) {
      return;
    }

    this.setState("finishing", this.finishRecordingButton);

    Effects.delayedFadeOut(this.videoStatesBlock);
    Effects.delayedFadeOut(this.videoMiniControlsBlock);
    Effects.delayedFadeOut(this.videoButtonsBlock);
    Effects.delayedFadeOut(this.recorderButtonsBlock);
    Effects.delayedFadeOut(this.previewToggleButton);
    Effects.delayedBlurIn(this.videoElement);

    this.timer.pause();
    this.recorder.stop();
    this.videoElement.pause();
    this.micMeterStop();

    EventBus.event("recording-session-finished");

    this.sessionFinalizedPromise = this.finalizeSession();
    await this.sessionFinalizedPromise;

    this.setState("finished");
  }

  // The last block is in the upload queue once the loop has ended (A12), and
  // the server hears about the end only after every part is uploaded (B11).
  // Resolves to true when the server confirmed it.
  async finalizeSession() {
    await this.blocksPromise;
    this.recorder.closeStreams();

    await APP.uploader.drained();
    return APP.playcocolaAPI.sendSignalSessionFinalized();
  }

  // Read by page.js before the thanks message
  sessionFinalized() {
    return this.sessionFinalizedPromise ?? Promise.resolve(true);
  }

  // The timed comment modal asks to pause while it is open. It only resumes a
  // pause it made itself, never one the tester made (A27).
  askingForPause(value) {
    console.log("askingForPause: ", value);
    if (value) {
      this.pauseRecording("comment");
    } else if (this.state === "paused" && this.pausedBy === "comment") {
      this.resumeRecording();
    }
  }
  // Session :: END

  // Errors :: INI
  // The only place that tells the tester about a capture or recording failure:
  // one message, chosen by the error name (A10, A25), and the checklist row
  // of the device that failed (C2).
  showRecordingError(error, device) {
    const problem = SetupCheck.describeProblem(error, device);

    if (problem?.rowText) {
      if (device === "screen") {
        this.setupCheck.update({ screenProblem: problem });
      } else if (device === "microphone") {
        this.setupCheck.update({ micProblem: problem });
      }
    }

    if (problem === null) {
      const details = Utils.normalizeError(error);
      MessageBus.debug(`RecordingManager: ${device} not shared (${details.name}: ${details.message})`);
      return;
    }

    MessageBus.error(SetupCheck.message(problem), error);
  }
  // Errors :: END

  // Mic Meter :: INI
  // Replaces the old scale animation of the toggle (C7)
  updateMicLevel() {
    const level = this.recorder.micAnalyzer?.getLevel() ?? 0;
    this.showMicLevel(level);

    if (level >= RecordingManager.MIC_HEARD_LEVEL) {
      this.setupCheck.update({ micHeard: true });
    }
  }

  showMicLevel(level) {
    this.micLevel.setAttribute("aria-valuenow", level);
    this.micLevelBar.style.width = `${level}%`;
  }

  micMeterStart() {
    clearInterval(this.micMeterIntervalID);
    this.micMeterIntervalID =
      setInterval(() => {
        this.updateMicLevel();
      }, 300);
  }

  micMeterStop() {
    clearInterval(this.micMeterIntervalID);
    this.showMicLevel(0);
  }
  // Mic Meter :: END

  // Recording info :: INI
  // How the session is being recorded, shown to the developer next to the
  // recording. Never a label: the window or tab title and the device names
  // stay here. The screen fields are null while the screen is not shared
  // (interrupted), so the gap doesn't count as "no game sound".
  recordingInfo(event) {
    const live = this.recorder.isScreenLive();
    const settings = live ? this.recorder.screenVideoTrack.getSettings() : {};
    const mediaRecorder = this.recorder.mediaRecorder;
    const profile = this.recorder.profile;
    const quality = this.setupCheck.facts.quality;

    return {
      event,
      // Which build recorded it, for the admins
      client_version: version,
      display_surface: live ? settings.displaySurface ?? null : null,
      game_audio: live ? this.recorder.screenAudioTrack !== null : null,
      microphone: this.recorder.micAudioTrack?.readyState === "live",
      profile: profile.name,
      target_height: profile.height,
      target_frame_rate: profile.fps,
      width: settings.width ?? null,
      height: settings.height ?? null,
      frame_rate: settings.frameRate ?? null,
      video_bits_per_second: mediaRecorder?.videoBitsPerSecond || profile.bitrate,
      audio_bits_per_second: mediaRecorder?.audioBitsPerSecond || SimpleRecorder.AUDIO_BITS_PER_SECOND,
      // Never getMimeType(): it throws, and picks a type as a side effect
      mime_type: mediaRecorder?.mimeType || this.recorder.mimeType || null,
      // The last test of this screen, if any
      frame_rate_test: quality && {
        capture_fps: RecordingManager.roundFps(quality.captureFps ?? null),
        average_fps: RecordingManager.roundFps(quality.averageFps ?? null),
        encode_fps: RecordingManager.roundFps(quality.encodeFps ?? null),
        stepped_down_to: quality.steppedDownTo?.name ?? null,
      },
    };
  }

  // At Start, at every Continue and at every mic change during the session.
  // One request at a time, so the server gets them in order.
  sendRecordingInfo(event) {
    if (!["recording", "paused", "interrupted"].includes(this.state)) {
      return;
    }

    let info;

    try {
      info = this.recordingInfo(event);
    } catch (error) {
      MessageBus.debug("RecordingManager.sendRecordingInfo() - " + Utils.normalizeError(error).message);
      return;
    }

    // A failed or unplugged mic that was already off changes nothing
    if (event === "microphone" && info.microphone === this.#lastRecordingInfo?.microphone) {
      return;
    }

    this.#lastRecordingInfo = info;
    this.#recordingInfoChain = this.#recordingInfoChain
      .then(() => APP.playcocolaAPI.sendRecordingInfo(info))
      .catch((error) => console.warn("RecordingManager.sendRecordingInfo()", error));
  }
  // Recording info :: END

  sendTracksInfo() {
    let codec = null;

    try {
      codec = this.recorder.getMimeType();
    } catch (error) {
      // told when the recorder starts
    }

    const { name, width, height, fps, bitrate } = this.recorder.profile;
    MessageBus.debug("Recording profile: " + JSON.stringify({ profile: name, width, height, fps, bitrate, codec }));

    if (this.recorder.screenVideoTrack) {
      MessageBus.debug("screenVideoTrack: " + JSON.stringify(this.recorder.screenVideoTrack.getSettings()));
    } else {
      MessageBus.debug("No screenVideoTrack");
    }

    if (this.recorder.screenAudioTrack) {
      MessageBus.debug("screenAudioTrack: " + JSON.stringify(this.recorder.screenAudioTrack.getSettings()));
    } else {
      MessageBus.debug("No screenAudioTrack");
    }

    if (this.recorder.micAudioTrack) {
      MessageBus.debug("micAudioTrack: " + JSON.stringify(this.recorder.micAudioTrack.getSettings()));
    } else {
      MessageBus.debug("No micAudioTrack");
    }
  }

  // // Video Track Settings :: INI
  // videoSettingsTrack() {
  //   if (this.recorder.screenVideoTrack) {
  //     const settings = this.recorder.screenVideoTrack.getSettings();
  //     const constraints = this.recorder.screenVideoTrack.getConstraints();

  //     console.log("VideoTrack.settings: ", settings);
  //     console.log("VideoTrack.constraints: ", constraints);
  //   }
  // }

  // videoSettingsTrackStart() {
  //   this.videoSettingsTrackIntervalID =
  //     setInterval(() => {
  //       this.videoSettingsTrack();
  //     }, 5000);
  // }

  // videoSettingsTrackStop() {
  //   this.micCheckBlock.style.transform = `scale(1,1)`;
  //   clearInterval(this.videoSettingsTrackIntervalID);
  // }
  // // Video Track Settings :: END
}
